/**
 * Custom Gym Banner — Matching Tee (V2) addon behavior.
 * Master checkbox at header level toggles slot visibility + opt-in state.
 * Heading price is static. The "(+ $X.XX)" suffix next to "Shirt Size:"
 * updates from the selected size button.
 *
 * Coexistence with V1 (<gs-v3-mix>) on the same page handled via
 * btn.dataset.cgbPairHandled flag — see _bindFormIntercept.
 */
(async () => {
  const { Core } = await importModule('Core');
  const { publishEvent } = await importModule('Utils');

  customElements.define('cgb-addon-pair', class extends Core {
    subscriptions = {
      'cart:update': '_onCartSettled',
      'cart:error': '_onCartSettled'
    };

    render() {
      this._mainFormId = this.dataset.mainFormId;
      this._mainForm = document.getElementById(this._mainFormId);
      this._initialPrice = this.dataset.initialPrice || '';
      this._initialSuffix = '(+ ' + this._initialPrice + ')';

      this.subscribe('cart:update', { global: true });
      this.subscribe('cart:error', { global: true });

      this._bindMasterToggle();
      this._bindSizeButtons();
      this._bindQuantity();
      this._bindFormIntercept();

      this._syncSlotVisibilityFromMaster();
    }

    _onCartSettled() {
      if (this._activeBtn) {
        this._activeBtn.classList.remove('!loading');
        this._activeBtn = null;
      }
    }

    _getMasterToggle() {
      return this.querySelector('[data-cgb-pair-toggle]');
    }

    _syncSlotVisibilityFromMaster() {
      const cb = this._getMasterToggle();
      const checked = !!(cb && cb.checked);
      this.querySelectorAll('[data-cgb-pair-slot]').forEach(slot => {
        slot.dataset.cgbChecked = String(checked);
      });
    }

    // ---- Master checkbox: header-level. Toggles all slots' visibility. ----
    _bindMasterToggle() {
      const cb = this._getMasterToggle();
      if (!cb) return;
      cb.addEventListener('change', () => {
        this._syncSlotVisibilityFromMaster();
        if (!cb.checked) this._resetSuffix();
      });
    }

    // ---- Size buttons: exclusive selection within a slot + live suffix update ----
    _bindSizeButtons() {
      this.querySelectorAll('[data-cgb-pair-slot]').forEach(slot => {
        const sizeBtns = Array.from(slot.querySelectorAll('[data-cgb-pair-size]'));
        const suffixEl = slot.querySelector('[data-cgb-pair-price-suffix]');
        sizeBtns.forEach(btn => {
          btn.addEventListener('click', e => {
            e.preventDefault();
            if (btn.disabled) return;
            sizeBtns.forEach(b => b.setAttribute('aria-pressed', 'false'));
            btn.setAttribute('aria-pressed', 'true');
            if (suffixEl && btn.dataset.price) {
              suffixEl.textContent = '(+ ' + btn.dataset.price + ')';
            }
          });
        });
      });
    }

    _resetSuffix() {
      this.querySelectorAll('[data-cgb-pair-price-suffix]').forEach(el => {
        el.textContent = this._initialSuffix;
      });
      // Also clear size selection so re-checking starts fresh-aware
      this.querySelectorAll('[data-cgb-pair-size][aria-pressed="true"]').forEach(b => {
        b.setAttribute('aria-pressed', 'false');
      });
    }

    // ---- Quantity: clamp to [1, max] ----
    _bindQuantity() {
      this.querySelectorAll('[data-cgb-pair-qty]').forEach(input => {
        const clamp = () => {
          const min = parseInt(input.min, 10) || 1;
          const max = parseInt(input.max, 10) || 99;
          let v = parseInt(input.value, 10);
          if (!Number.isFinite(v) || v < min) v = min;
          if (v > max) v = max;
          input.value = String(v);
        };
        input.addEventListener('change', clamp);
        input.addEventListener('blur', clamp);
      });
    }

    // ---- Cart: collect this V2's items (only if master checkbox checked) ----
    _collectV2Items() {
      const cb = this._getMasterToggle();
      if (!cb || !cb.checked) return [];                       // master unchecked → no V2 addons
      const items = [];
      this.querySelectorAll('[data-cgb-pair-slot]').forEach(slot => {
        const pressed = slot.querySelector('[data-cgb-pair-size][aria-pressed="true"]');
        if (!pressed) return;                                   // silent skip per spec
        const variantId = parseInt(pressed.dataset.variantId, 10);
        if (!variantId) return;
        const qtyInput = slot.querySelector('[data-cgb-pair-qty]');
        const qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
        items.push({ id: variantId, quantity: qty });
      });
      return items;
    }

    // ---- Read V1's selected pills directly so we can merge in one cart:add ----
    _collectV1Items() {
      const pills = document.querySelectorAll(
        '[data-gs-v3-addon-card][data-expanded="true"] [data-gs-v3-addon-size][aria-pressed="true"]'
      );
      const items = [];
      const seen = new Set();
      pills.forEach(p => {
        const id = parseInt(p.dataset.variantId, 10);
        if (!id || seen.has(id)) return;
        seen.add(id);
        items.push({ id, quantity: 1 });
      });
      return items;
    }

    _getMainVariantId() {
      let input = null;
      if (this._mainForm && this._mainForm.elements) {
        input = this._mainForm.elements.namedItem('id');
      }
      if (!input) {
        input = document.querySelector(
          `[data-element="id-input"][form="${this._mainFormId}"]`
        );
      }
      return input ? parseInt(input.value, 10) || null : null;
    }

    _getMainQty() {
      const qtyInput = document.querySelector(
        `[name="quantity"][form="${this._mainFormId}"]`
      ) || (this._mainForm && this._mainForm.querySelector('[name="quantity"]'));
      return qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    }

    // ---- Read line-item properties (properties[X]) attached to main form ----
    // Required because we preventDefault() the native form submit; without this,
    // cgb_custom_text inputs and similar properties[] fields would never reach
    // /cart/add.js → order.line_items[i].properties stays empty.
    _collectMainProperties() {
      const props = {};
      const inputs = document.querySelectorAll(
        `[name^="properties["][form="${this._mainFormId}"]`
      );
      inputs.forEach(el => {
        if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
        const m = el.name.match(/^properties\[(.+)\]$/);
        if (!m) return;
        if (el.type === 'file') {
          // el.value is a fake path — pass the File itself so cart-provider can
          // switch to a multipart request and let Shopify host the upload.
          if (el.files && el.files[0]) props[m[1]] = el.files[0];
          return;
        }
        const value = el.value;
        if (value === '' || value == null) return;
        props[m[1]] = value;
      });
      return props;
    }

    _bindFormIntercept() {
      this._clickHandler = e => {
        const btn = e.target.closest('[data-to-cart]');
        if (!btn) return;
        if (btn.dataset.cgbPairHandled === 'true') return;     // already claimed

        const v2Items = this._collectV2Items();
        const v1Items = this._collectV1Items();
        const addonItems = [...v2Items, ...v1Items];
        if (addonItems.length === 0) return;                    // nothing to merge — let default flow

        const mainId = this._getMainVariantId();
        if (!mainId) return;

        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();

        btn.dataset.cgbPairHandled = 'true';
        setTimeout(() => { delete btn.dataset.cgbPairHandled; }, 100);

        btn.classList.add('!loading');
        this._activeBtn = btn;

        const mainItem = { id: mainId, quantity: this._getMainQty() };
        const mainProps = this._collectMainProperties();
        if (Object.keys(mainProps).length > 0) mainItem.properties = mainProps;
        const items = [mainItem, ...addonItems];
        publishEvent('cart:add', { items }, null);
      };
      document.addEventListener('click', this._clickHandler, true);
    }

    disconnect() {
      if (this._clickHandler) {
        document.removeEventListener('click', this._clickHandler, true);
      }
    }
  });
})();
